import { createParser } from "eventsource-parser";

import { processStreamResponse } from "@intelligems/ig-utils/stream-utils";
import { aiApiClient } from "@src/api/ai-api";
import { getAuthToken } from "@src/utils";

/**
 * The two calls an agent conversation is made of.
 *
 * `POST /agents/start` accepts the turn and returns immediately; the work runs
 * in the background and everything it produces comes back over
 * `GET /agents/sessions/:id/stream`. So a turn is always start-then-stream,
 * and the response to the start tells you which session to stream.
 *
 * The stream is per-turn, not per-session: the server closes it once the turn
 * reaches a terminal state. Reconnecting mid-turn, or opening the next turn's
 * stream, is the same call with the cursor the client has been keeping.
 */

export interface AgentTurnStarted {
  sessionId: string;
  turnId: string;
  turnNumber: number;
  status: "queued" | "running";
}

export async function startAgentTurn(params: {
  agentName: string;
  input?: unknown;
  /** Omit on the first turn — the server creates the session and returns its id. */
  sessionId?: string;
  title?: string;
}): Promise<AgentTurnStarted> {
  const { data } = await aiApiClient.post<AgentTurnStarted>(
    "/agents/start",
    params
  );
  return data;
}

/** What `GET /agents/sessions/:id` answers: the session and its current turn. */
export interface AgentSessionSnapshot {
  session: {
    id: string;
    agentName: string;
    state: Record<string, unknown>;
    currentTurnNumber: number;
  };
  currentTurn: { id: string; status: string } | null;
}

/** The session this user started last for the agent in this store, or null. */
export async function getLatestAgentSession(
  agentName: string
): Promise<AgentSessionSnapshot | null> {
  const { data } = await aiApiClient.get<{
    session: AgentSessionSnapshot | null;
  }>("/agents/sessions/latest", { params: { agentName } });
  return data.session;
}

export async function getAgentSession(
  sessionId: string
): Promise<AgentSessionSnapshot> {
  const { data } = await aiApiClient.get<AgentSessionSnapshot>(
    `/agents/sessions/${sessionId}`
  );
  return data;
}

/**
 * Streams one turn, calling `onFrame` for every frame the server ships.
 *
 * Resolves when the server closes the stream, which it does at the turn's
 * terminal event. Rejects on transport failure so the caller can decide
 * whether to reconnect — this deliberately does not retry on its own, because
 * whether a reconnect is wanted depends on the cursor the caller holds.
 *
 * Frames are reassembled into the shape the server's own `AgentStreamFrame`
 * union has: the SSE event name is the discriminator on the wire (`data` is
 * the bare envelope), while every consumer wants the tagged frame back.
 */
export async function streamAgentSession(params: {
  sessionId: string;
  /** Resume point. Both default to "0", which streams the session from its start. */
  afterSequence?: string;
  afterStreamSeq?: string;
  onFrame: (frame: unknown) => void;
  signal: AbortSignal;
}): Promise<void> {
  const token = getAuthToken();
  const query = new URLSearchParams({
    afterSequence: params.afterSequence ?? "0",
    afterStreamSeq: params.afterStreamSeq ?? "0",
  });

  const response = await aiApiClient.get(
    `/agents/sessions/${params.sessionId}/stream?${query.toString()}`,
    {
      adapter: "fetch",
      headers: {
        Accept: "text/event-stream",
        ...(token ? { Authorization: token } : {}),
      },
      responseType: "stream",
      signal: params.signal,
    }
  );

  const parser = createParser({
    onEvent: (event) => {
      const frame = toStreamFrame(event.event, event.data);
      if (frame !== null) {
        params.onFrame(frame);
      }
    },
  });

  await processStreamResponse(response, parser);
}

/**
 * The wire drops the frame's own discriminator: `better-sse` puts it in the
 * SSE event name and ships the payload bare. Put it back, so consumers dispatch
 * on `kind` exactly as the server's type says they should.
 */
function toStreamFrame(eventName: string | undefined, data: string): unknown {
  if (!data) {
    return null;
  }

  let payload: unknown;
  try {
    payload = JSON.parse(data);
  } catch {
    // A frame that does not parse is one frame lost, not a broken stream.
    return null;
  }

  switch (eventName) {
    case "agent.envelope":
      return { kind: "envelope", envelope: payload };
    case "agent.cursor.advance":
      return { kind: "cursor.advance", ...(payload as object) };
    case "agent.retention.evicted":
      return { kind: "retention.evicted", ...(payload as object) };
    case "agent.session.at_rest":
      return { kind: "session.at_rest", ...(payload as object) };
    default:
      return null;
  }
}
